"use client"
import { format, isBefore, startOfToday } from "date-fns"
import Icons8Search from "@/src/components/icons/icons8/icons8Search"
import { Button } from "@/src/components/ui/button"
import { ICONS, Input } from "@/src/components/ui/input"
import { Calendar } from "@/src/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/src/components/ui/popover"
import useScreenSize from "@/src/hooks/useScreenSize"
import LocalityInput from "@/src/components/custom/locality-input"
import { useForm, Controller } from "react-hook-form"
import { useAppDispatch } from "@/src/redux/store"
import { SethomePageSearchClicked } from "@/src/redux/utils"
import { FilterHomePageListingRequest, PUBLIC_LISTING_CATEGORIES } from "@/src/redux/listings/features"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/ui/select"
import { useRouter, useSearchParams } from "next/navigation"

// Only include the relevant parts of FilterState
export interface SearchFilters {
  location?: string
  startDate?: Date
  endDate?: Date
  type?: string
}

const SearchListings = () => {
  const { width } = useScreenSize()
  const isSmall = width < 768
  const today = startOfToday()
  const dispatch = useAppDispatch()
  const router = useRouter()
    const searchParams = useSearchParams()

  // Set up form with react-hook-form
  const { setValue, watch, handleSubmit, control } = useForm<SearchFilters>({
    defaultValues: {
      location: "",
      startDate: undefined,
      endDate: undefined,
      type: "",
    },
  })

  // Watch the form values
  const startDate = watch("startDate")
  const endDate = watch("endDate")

  const CATEGORY_OPTIONS_FLAT = [
  { label: "ALL SPACES", value: "all", enumValue: PUBLIC_LISTING_CATEGORIES.ALL },
  { label: "RESIDENCES", value: "residence", enumValue: PUBLIC_LISTING_CATEGORIES.RESIDENCE },
  { label: "Beachfront Villas", value: "beachfront_villas", enumValue: PUBLIC_LISTING_CATEGORIES.BEACHFRONT_VILLAS },
  { label: "Apartments", value: "apartments", enumValue: PUBLIC_LISTING_CATEGORIES.APARTMENTS },
  { label: "RETAIL (Pop-ups)", value: "retail", enumValue: PUBLIC_LISTING_CATEGORIES.RETAIL },
  { label: "EXPERIENCES", value: "experiences-section", enumValue: PUBLIC_LISTING_CATEGORIES.EXPERIENCES },
  { label: "Sightseeing Tours", value: "sightseeing_tours", enumValue: PUBLIC_LISTING_CATEGORIES.SIGHTSEEING_TOURS },
  { label: "Private Boat Cruise", value: "private_boat_cruise", enumValue: PUBLIC_LISTING_CATEGORIES.PRIVATE_BOAT_CRUISE },
  { label: "Other Experiences", value: "other_experiences", enumValue: PUBLIC_LISTING_CATEGORIES.OTHER_EXPERIENCES },
  { label: "EVENT CENTERS", value: "event_center", enumValue: PUBLIC_LISTING_CATEGORIES.EVENT_CENTER },
  { label: "ICONIC SPACES", value: "iconic", enumValue: PUBLIC_LISTING_CATEGORIES.ICONIC },
]

  // Handle start date change
  const handleStartDateChange = (date: Date | undefined) => {
    setValue("startDate", date)

    // If end date exists and is before the new start date, reset end date
    if (date && endDate && isBefore(endDate, date)) {
      setValue("endDate", undefined)
    }
  }

  // Handle end date change
  const handleEndDateChange = (date: Date | undefined) => {
    if (date && startDate && isBefore(date, startDate)) {
      return
    }
    setValue("endDate", date)
  }

  // Handle form submission
  const onSubmit = (data: SearchFilters) => {
     const params = new URLSearchParams(searchParams.toString())

  // Example: clear category if it exists
  if (params.has("category")) {
    params.delete("category")
  }

  // If after deleting there are no params left, go to root `/`
  const newUrl = params.toString() ? `?${params.toString()}` : "/"
  router.replace(newUrl)
    console.log("Search filters:", data)
    dispatch(FilterHomePageListingRequest(data))
    dispatch(SethomePageSearchClicked(true))
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="pt-7.5 md:pt-12.5 gap-1.25 flex flex-col items-center"
    >
      <div className="flex md:flex-row flex-col gap-1.25 lg:gap-0 min-w-[300px]">
        {/* Location Input */}
        <LocalityInput
          form={{ setValue, watch, getFieldState: () => ({ error: undefined }) } as any}
          name="location"
        />

        {/* Start Date Calendar */}
        <Popover>
          <PopoverTrigger asChild>
            <div className="relative w-full md:w-[250px]">
              <Input
                inputSize={isSmall ? "md" : "lg"}
                prefixIcon={ICONS.CALENDAR}
                className="bg-black w-full bg-opacity-10 backdrop-blur-lg text-white h-fit placeholder:text-[rgba(255,255,255,0.6)] border-opacity-20 cursor-pointer"
                placeholder="Start Date"
                iconsClassName="fill-white"
                value={startDate ? format(startDate, "PPP") : ""}
                readOnly
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              className="w-[250px]"
              selected={startDate}
              onSelect={handleStartDateChange}
              disabled={(date) => isBefore(date, today)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* End Date Calendar */}
        <Popover>
          <PopoverTrigger asChild>
            <div className="relative w-full md:w-[250px]">
              <Input
                inputSize={isSmall ? "md" : "lg"}
                prefixIcon={ICONS.CALENDAR}
                className="bg-white h-fit w-full cursor-pointer"
                placeholder="End Date"
                value={endDate ? format(endDate, "PPP") : ""}
                readOnly
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              className="w-[250px]"
              selected={endDate}
              onSelect={handleEndDateChange}
              disabled={(date) =>
                isBefore(date, today) || (startDate ? isBefore(date, startDate) : false)
              }
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Type Select */}
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value} >
              <SelectTrigger className=" md:w-[200px] bg-black h-fitbg-black w-full bg-opacity-10 backdrop-blur-lg text-white h-fit placeholder:text-[rgba(255,255,255,0.6)] border-opacity-20 cursor-pointer min-h-[80px] text-lg">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>

              <SelectContent >
                {
                  CATEGORY_OPTIONS_FLAT.map(e=>(
                    <SelectItem key={e.enumValue} value={e.enumValue}>{e.label}</SelectItem>
                  ))
                }

              </SelectContent>
            </Select>
          )}
        />
      </div>

      <Button
        type="submit"
        className="w-20 h-20 md:w-25 md:h-25 primary-gradient mt-5 rounded-full"
      >
        <Icons8Search className="fill-white h-7.5" />
      </Button>
    </form>
  )
}

export default SearchListings

